import { Gift, RefreshCw, UserX } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { rewardSentence } from '@/features/member-get-member/model/copy';
import { accentStyle } from '@/features/member-get-member/public/brand';
import { buildShareText, isInAppWebView, shareChannels } from '@/features/member-get-member/public/share';
import { BenefitsCard } from '@/features/member-get-member/public/participant/benefits-card';
import { HowItWorks } from '@/features/member-get-member/public/participant/how-it-works';
import { ReferralList } from '@/features/member-get-member/public/participant/referral-list';
import { ShareCard } from '@/features/member-get-member/public/participant/share-card';
import { StatsStrip } from '@/features/member-get-member/public/participant/stats-strip';
import { useParticipantMgm } from '../../api/use-participant';
import { PromoHeroCard } from '../promo-hero-card';

interface MgmTabProps {
  idPassbook: string;
}

function Notice({ icon: Icon, title, text }: { icon: typeof Gift; title: string; text: string }) {
  return (
    <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-2 p-8 text-center">
      <span className="mgm-accent-soft grid size-12 place-items-center rounded-2xl">
        <Icon className="mgm-accent-text size-6" />
      </span>
      <p className="font-medium">{title}</p>
      <p className="max-w-xs text-sm text-muted-foreground">{text}</p>
    </Card>
  );
}

/**
 * "Indique e ganhe" dentro da área: os mesmos cards da página `/mgm/:id` do app,
 * com o participante vindo da sessão (o BFF chama a legacy com a chave do MGM).
 */
export function MgmTab({ idPassbook }: MgmTabProps) {
  const { data: view, isPending, isError, refetch, isRefetching } = useParticipantMgm(idPassbook);

  if (isPending) {
    return (
      <div className="mx-auto w-full max-w-xl space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-56 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }

  if (isError || !view) {
    return (
      <Card className="mx-auto flex w-full max-w-xl flex-col items-center gap-3 p-8 text-center">
        <p className="font-medium">Não foi possível carregar o Indique e ganhe.</p>
        <Button variant="outline" onClick={() => void refetch()} loading={isRefetching}>
          <RefreshCw className="size-4" />
          Tentar de novo
        </Button>
      </Card>
    );
  }

  const brandName = view.brand?.name ?? '';

  return (
    <div className="mgm-scope mx-auto w-full max-w-xl space-y-4" style={accentStyle(view.brand)}>
      {view.status === 'inactive' ? (
        <Notice
          icon={Gift}
          title="Nenhuma campanha no momento"
          text={`O Indique e ganhe${brandName ? ` da ${brandName}` : ''} não está ativo agora. Volte em breve.`}
        />
      ) : view.status === 'not_member' ? (
        <Notice icon={UserX} title="Cadastro não encontrado" text="Não encontramos seu cadastro no programa." />
      ) : view.rules && view.code && view.shareUrl ? (
        <>
          <PromoHeroCard
            icon={Gift}
            eyebrow="Indique e ganhe"
            title={view.rules.title || 'Convide amigos e ganhe'}
            accentColor={view.brand?.backgroundColor ?? undefined}
            foregroundColor={view.brand?.foregroundColor ?? undefined}
          >
            {view.rules.subtitle || 'Compartilhe seu link e acompanhe suas recompensas.'}
          </PromoHeroCard>
          <BenefitsCard rules={view.rules} />
          <ShareCard
            url={view.shareUrl}
            code={view.code}
            shareText={buildShareText({
              brandName,
              benefit: view.rules.referred.enabled
                ? rewardSentence('referred', view.rules.referred.amountText, view.rules.referred.trigger, 'referred')
                : null,
              url: view.shareUrl,
            })}
            brandName={brandName}
            channels={shareChannels(window, false)}
            inApp={isInAppWebView(window, false)}
          />
          <StatsStrip
            indicated={view.stats?.indicated ?? 0}
            registered={view.stats?.registered ?? 0}
            converted={view.stats?.converted ?? 0}
            earnedText={view.stats?.earnedText ?? null}
          />
          <ReferralList items={view.referrals ?? []} />
          <HowItWorks rules={view.rules} />
        </>
      ) : (
        <Notice icon={Gift} title="Nenhuma campanha no momento" text="Volte em breve." />
      )}
    </div>
  );
}
